<template>
    <div>
        <b-breadcrumb class="text-capitalize">
            <b-breadcrumb-item :to="{ name: 'Dashboard' }"><font-awesome-icon icon="caret-right" class="mr-1" />Dashboard</b-breadcrumb-item>
        </b-breadcrumb>

      <b-skeleton-wrapper :loading="isLoading">
        <template #loading>
          <b-card>
            <b-skeleton width="85%"></b-skeleton>
            <b-skeleton width="55%"></b-skeleton>
            <b-skeleton width="70%"></b-skeleton>
          </b-card>
        </template>
        <div class="container-fluid px-4">
            <b-card class="mb-1" >
                <b-card-title>
                    <span>{{ organizationName }}</span>
                    <b-button size="sm" class="mx-2" style="border-width: 0px" variant="outline-secondary"
                              :disabled="isLoadingError || isLoading"
                              v-on:click.stop="onEditOrganization()">
                        <font-awesome-icon icon="edit" />
                    </b-button>
                </b-card-title>
              <b-card-sub-title>
                <span>Dashboard</span>
              </b-card-sub-title>
            </b-card>
        </div>
      </b-skeleton-wrapper>

        <b-overlay :show="isLoading" rounded="sm">

            <ErrorView :visible="isLoadingError" :errorValue="loadingErrorValue"/>

            <div v-if="isLoadingError == false" class="container-fluid px-4">
            <div class="row">
                <div class="col-6">
                    <b-card class="mb-2" no-body
                            border-variant="dark"
                            header-text-variant="white"
                            header-bg-variant="dark">
                        <template v-slot:header>
                            <span>Sessions - Last Four Weeks</span>
                        </template>
                        <div class="px-2 pt-3" style="border-bottom: 1px solid #CCCCCC">
                            <column-chart
                                    stacked="true"
                                    :data="sessionCountsWeekly"
                                    :dataset="{
                                            borderWidth: 1,
                                            pointRadius: 1,
                                          }"
                                    :library="{
                                        animation: {easing: 'easeOutQuart'},
                                        legend:{position: 'bottom'}
                                        }"
                                    xtitle=""
                                    :messages="{empty: 'No sessions found.'}"/>
                        </div>

                        <div v-if="hasSessionCountsWeekly" class="d-flex flex-wrap m-1 ">
                            <div class="d-flex justify-content-between align-items-center py-3 px-2 m-1" style="width: 175px; border: 1px solid #CCCCCC; " v-for="(item) in sessionCountsWeekly" :key="item.name">
                                <small>{{item.name}}</small>
                                <b-badge variant="primary" pill>{{item.count}}</b-badge>
                            </div>
                            <div class="d-flex justify-content-between align-items-center py-3 px-2 m-1" style="width: 175px; border: 1px solid #CCCCCC; ">
                                <small>Total</small>
                                <b-badge variant="primary" pill>{{sessionCountsWeeklyTotal}}</b-badge>
                            </div>
                        </div>
                    </b-card>
                </div>

                <div class="col-6">
                    <b-card class="mb-2" no-body
                            border-variant="dark"
                            header-text-variant="white"
                            header-bg-variant="dark">
                        <template v-slot:header>
                            <span>Cumulative Sessions</span>
                        </template>
                        <div class="px-2 pt-3" style="border-bottom: 1px solid #CCCCCC">
                            <line-chart
                                    :data="sessionCountsMonthly"
                                    :dataset="{
                                            borderWidth: 1,
                                            pointRadius: 1,
                                            fill: true,
                                          }"
                                    :library="{
                                            animation: {easing: 'easeOutQuart'},
                                            legend: { position: 'bottom'},
                                            scales: {
                                              yAxes: [{
                                                stacked: true,
                                              }]
                                            },}"
                                    xtitle=""
                                    :messages="{empty: 'No sessions found.'}"/>
                        </div>

                        <div v-if="hasSessionCountsMonthly" class="d-flex flex-wrap m-1">
                            <div class="d-flex justify-content-between align-items-center py-3 px-2 m-1" style="width: 175px; border: 1px solid #CCCCCC; " v-for="(item) in sessionCountsMonthly" :key="item.name">
                                <small>{{item.name}}</small>
                                <b-badge variant="primary" pill>{{item.count}}</b-badge>
                            </div>
                            <div class="d-flex justify-content-between align-items-center py-3 px-2 m-1" style="width: 175px; border: 1px solid #CCCCCC; ">
                                <small>Total</small>
                                <b-badge variant="primary" pill>{{sessionCountsMonthlyTotal}}</b-badge>
                            </div>
                        </div>
                    </b-card>
                </div>

            </div>

            <div class="row">
                <div class="col-6">
                    <b-card class="mb-2" no-body
                            border-variant="dark"
                            header-text-variant="white"
                            header-bg-variant="dark">
                        <template v-slot:header>
                            <span class="mb-0"> <font-awesome-icon icon="chart-bar" class="mr-1" /> Recent Sessions</span>
                        </template>

                        <b-list-group flush>
                            <b-list-group-item v-for="(item, index) in recentSessions" :key="index"
                                               :to="{ name: 'Session', params: { sessionId: item.sessionId, orgId: organizationId, athleteId: item.athleteId} }">
                                <span v-if="item.sessionId">{{index + 1}}.</span> {{item.name.truncate(32)}}
                            </b-list-group-item>
                        </b-list-group>

                    </b-card>
                </div>
                <div class="col-6">
                    <b-card class="mb-2" no-body
                            border-variant="dark"
                            header-text-variant="white"
                            header-bg-variant="dark">
                        <template v-slot:header>
                            <span class="mb-0"> <font-awesome-icon icon="running" class="mr-1" /> Recent Athletes</span>
                        </template>
                        <b-list-group flush>
                            <b-list-group-item v-for="(item, index) in recentAthletes" :key="index"
                                               :to="{ name: 'SessionList', params: { orgId: organizationId, athleteId: item.athleteId} }">
                                <span v-if="item.athleteId">{{index + 1}}.</span> {{item.name.truncate(32)}}
                            </b-list-group-item>
                        </b-list-group>

                    </b-card>
                </div>
            </div>

        </div>
        </b-overlay>

        <OrganizationModal/>

    </div>
</template>

<script>

    import moment from 'moment'
    import { mapGetters, mapActions } from 'vuex'
    import ErrorView from '../ErrorView'
    import OrganizationModal from './OrganizationModal'

    export default {
        name: "DashboardPage",
        components: {
            OrganizationModal,
            ErrorView
        },
        data () {
            return {
                organizationId: this.$route.params.orgId,
            }
        },
        created () {
            if(!this.organizationId) {
                this.$router.push({name: 'OrganizationList'});
            } else {
                this.refreshOrganization();
                this.refreshDashboard();
            }
        },
        computed: {
            ...mapGetters({
                organization: 'organization/current',
                organizationName: 'organization/currentName',
                organizationLoading: 'organization/currentLoading',
                organizationLoadingError: 'organization/currentLoadingError',
                organizationLoadingErrorValue: 'organization/currentLoadingErrorValue',

                dashboard: 'dashboard/current',
                dashboardLoading: 'dashboard/currentLoading',
                dashboardLoadingError: 'dashboard/currentLoadingError',
                dashboardLoadingErrorValue: 'dashboard/currentLoadingErrorValue',
            }),

            isLoading: function() {
                return this.dashboardLoading || this.organizationLoading;
            },
            isLoadingError: function() {
                return (this.dashboardLoadingError || this.organizationLoadingError) || (!this.isLoading && (!this.organization || !this.dashboard));
            },
            loadingErrorValue: function() {
                if(this.dashboardLoadingErrorValue)
                    return this.dashboardLoadingErrorValue;

                return this.organizationLoadingErrorValue;
            },


            recentSessions: function() {
                if(this.dashboard && this.dashboard.recentSessions && this.dashboard.recentSessions.length) {
                    return this.dashboard.recentSessions;
                }

                return [{ athleteId: null, sessionId: null, name:'-' },];
            },

            recentAthletes: function() {
                if(this.dashboard && this.dashboard.recentSessions && this.dashboard.recentSessions.length) {
                    return this.dashboard.recentAthletes;
                }

                return [{ athleteId: null, name:'-' },];
            },

            hasSessionCountsWeekly: function() {
              return this.sessionCountsWeekly.find(x => x.count > 0);
            },

            sessionCountsWeekly: function() {

                let stepTestData = {name: 'Graded Exercise Test', color: '#390099', count: 0, data: {}};
                let restingData = {name: 'Resting (RMR)',  color: '#9e0059', count: 0, data: {}};
                let fiveOneFiveData = {name: '5-1-5 (Acyclic)',  color: '#ff0054', count: 0, data: {}};
                let intervalsData = {name: 'Intervals (Acyclic)',  color: '#ff5400', count: 0, data: {}};
                let freeData = {name: 'Free',  color: '#ffbd00', count: 0, data: {}};

                //data: {'June 1 - 7': 3, 'June 8 - 14': 4, 'June 15 - 22': 4, 'June 23 - 30': 6}/
                if(this.dashboard && this.dashboard.weeklySessionCounts && this.dashboard.weeklySessionCounts.length) {

                    this.dashboard.weeklySessionCounts.forEach(function(value) {

                        let start = moment(value.start);
                        let end = moment(value.end);

                        let key = start.format('MMM DD');
                        if(start.month() != end.month()) {
                            key += ' - ' + end.format('MMM DD');
                        } else {
                            key += ' - ' + end.format('DD');
                        }

                        stepTestData.data[key] = value.stepTest;
                        stepTestData.count += value.stepTest;

                        restingData.data[key] = value.restingTest;
                        restingData.count += value.restingTest;

                        fiveOneFiveData.data[key] = value.fiveOneFive;
                        fiveOneFiveData.count += value.fiveOneFive;

                        intervalsData.data[key] = value.intervalTest;
                        intervalsData.count += value.intervalTest;

                        freeData.data[key] = value.freeTest;
                        freeData.count += value.freeTest;

                    });
                }

                return [stepTestData, restingData, fiveOneFiveData, intervalsData, freeData];
            },

            sessionCountsWeeklyTotal: function() {
                return this.sessionCountsWeekly.reduce((accumulator, currentValue) => accumulator + currentValue.count, 0);
            },

            hasSessionCountsMonthly: function() {
              return this.sessionCountsMonthly.find(x => x.count > 0);
            },

            sessionCountsMonthly: function() {


                let stepTestData = {name: 'Graded Exercise Test', color: '#390099', count: 0, data: {}};
                let restingData = {name: 'Resting (RMR)',  color: '#9e0059', count: 0, data: {}};
                let fiveOneFiveData = {name: '5-1-5 (Acyclic)',  color: '#ff0054', count: 0, data: {}};
                let intervalsData = {name: 'Intervals (Acyclic)',  color: '#ff5400', count: 0, data: {}};
                let freeData = {name: 'Free',  color: '#ffbd00', count: 0, data: {}};

                if(this.dashboard && this.dashboard.monthlySessionCounts && this.dashboard.monthlySessionCounts.length) {

                    this.dashboard.monthlySessionCounts.forEach(function(value) {

                        let start = moment(value.start);

                        let key = start.format('MMM');

                        stepTestData.data[key] = value.stepTest;
                        stepTestData.count += value.stepTest;

                        restingData.data[key] = value.restingTest;
                        restingData.count += value.restingTest;

                        fiveOneFiveData.data[key] = value.fiveOneFive;
                        fiveOneFiveData.count += value.fiveOneFive;

                        intervalsData.data[key] = value.intervalTest;
                        intervalsData.count += value.intervalTest;

                        freeData.data[key] = value.freeTest;
                        freeData.count += value.freeTest;

                    });
                }

                return [stepTestData, restingData, fiveOneFiveData, intervalsData, freeData];
            },

            sessionCountsMonthlyTotal: function() {
                return this.sessionCountsMonthly.reduce((accumulator, currentValue) => accumulator + currentValue.count, 0);
            }


        },
        methods: {
            ...mapActions({
                selectOrganization: 'organization/select',
                selectDashboard: 'dashboard/select',
                markDirtyDashboard: 'dashboard/markDirty'
            }),
            refreshOrganization() {
                this.selectOrganization(this.organizationId);
            },
            refreshDashboard() {
                this.markDirtyDashboard();
                this.selectDashboard(this.organizationId);
            },

            onEditOrganization() {
                this.$bvModal.show('edit-organization-modal')
            },
        }
    }
</script>

<style scoped>

</style>